import apiGraph from "@/helpers/api/apiGraph";
import graphHelper from "@/helpers/utilities/GraphHelper";

import { Submission } from "@/models/Education/index";
import LogHandler from "@/helpers/utilities/Handlers/LogHandler";

/**
 *  Actions for the submissions and outcomes store
 *
 * @module Assignments
 * @category Store
 * @subcategory Assignments
 * @name Actions
 */

export default {
  async loadSubmissions({ commit }, assignmentList) {
    const arr = [];
    for (let i = 0; i < assignmentList.length; i++) {
      arr.push(Submission.generateGraphRequestFromAssignment(assignmentList[i]));
    }

    const result = await graphHelper.makeBatchRequest(arr);
    result.map(resp => {
      if (resp.status === 200) {
        const submissions = resp.body.value.map(s => Submission.prepareForSkolebordet(s));

        commit("loadSubmissions", { assignmentId: resp.id, submissions });
      }
    });
  },

  async loadSubmission({ commit }, { extSysApiSectionId, assignmentId, submissionId }) {
    try {
      const result = await apiGraph.get(
        `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}?$expand=resources,submittedResources,outcomes`
      );
      const submission = Submission.prepareForSkolebordet(result.data);
      commit("loadSubmission", { assignmentId, submission });
    } catch (e) {
      LogHandler.handleError(e);
    }
  },

  async submitSubmission({ dispatch }, { extSysApiSectionId, assignmentId, submissionId }) {
    await apiGraph.post(
      `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/submit`
    );
    await dispatch("loadSubmission", { extSysApiSectionId, assignmentId, submissionId });
  },

  async undoSubmitSubmission({ dispatch }, { extSysApiSectionId, assignmentId, submissionId }) {
    await apiGraph.post(
      `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/unsubmit`
    );
    await dispatch("loadSubmission", { extSysApiSectionId, assignmentId, submissionId });
  },

  // eslint-disable-next-line no-empty-pattern
  async updateSubmissionResources({ dispatch }, { extSysApiSectionId, assignmentId, submissionId, resources }) {
    const promises = [];

    if (resources) {
      resources
        .filter(res => res.isDirty)
        .forEach(res => {
          if (res.delete) {
            promises.push(
              apiGraph
                .delete(
                  `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/resources/${res.id}`
                )
                .then(() => {
                  res.isDirty = false;
                })
            );
          } else if (!res.id) {
            promises.push(
              apiGraph
                .post(
                  `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/resources`,
                  res.requestPayload
                )
                .then(() => {
                  res.isDirty = false;
                })
            );
          }
        });
    }

    await Promise.all(promises);
    await dispatch("loadSubmission", { extSysApiSectionId, assignmentId, submissionId });
  },

  async publishSubmission({ dispatch }, { extSysApiSectionId, assignmentId, submissionId }) {
    await apiGraph.post(
      `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/return`
    );

    await dispatch("loadSubmission", { extSysApiSectionId, assignmentId, submissionId });
  },

  /**
   * Create resources folder on assignment
   *
   * @param { object } vuex Vuex
   * @param { vuex } vuex.getters Getters
   * @param { vuex } vuex.dispatch Dispatch
   * @param { vuex } vuex.commit Commit
   * @param { object } payload Payload
   * @param {  payload } payload.extSysApiSectionId Id of the section the assignment is related to
   * @param {  payload } payload.assignmentId Id of the assignment the submission belongs to
   * @param {  payload } payload.submissionId Id of the submission
   * @returns {Promise<string>} Promise with weburl for assignment resourcesfolder
   */
  async createSubmissionResourcesFolder(
    { getters, dispatch, commit },
    { extSysApiSectionId, assignmentId, submissionId }
  ) {
    const noOfRetry = 5;
    let levelOfRetry = 0;

    if (!assignmentId || !extSysApiSectionId) {
      throw new Error("Input not valid");
    }

    if (
      getters.SUBMISSION(assignmentId, submissionId).resourcesFolderUrl &&
      getters.SUBMISSION(assignmentId, submissionId).resourcesFolderUrl !== ""
    ) {
      return getters.SUBMISSION(assignmentId, submissionId).resourcesFolderUrl;
    }

    try {
      const result = await apiGraph
        .post(
          `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/setUpResourcesFolder`
        )
        .catch(async e => {
          if (
            e.response &&
            e.response.status === 400 &&
            e.response.data.error.innerError &&
            e.response.data.error.innerError.code === "folderAlreadyExists"
          ) {
            await dispatch("loadSubmission", { extSysApiSectionId, assignmentId, submissionId });
            return;
          }
        });

      if (result && result.status === 200 && result.data && result.data.resourcesFolderUrl) {
        commit("createSubmissionResourcesFolder", {
          assignmentId,
          submissionId,
          resourcesFolderUrl: result.data.resourcesFolderUrl
        });
        return;
      }
    } catch {
      if (noOfRetry > levelOfRetry) {
        levelOfRetry = levelOfRetry + 1;
        return await setTimeout(async () => {
          return await dispatch("executeCreateSubmissionResourcesFolder", {
            extSysApiSectionId,
            assignmentId,
            submissionId
          });
        }, 2000);
      }
      throw new Error();
    }
  },

  /***********************************
   * Outcome
   ***********************************/

  // eslint-disable-next-line no-empty-pattern
  async updateOutcomes({}, { extSysApiSectionId, assignmentId, submissionId, outcomes }) {
    if (Array.isArray(outcomes)) {
      const promises = outcomes.map(async outcome => {
        if (outcome["@odata.type"] === "#microsoft.graph.educationFeedbackOutcome") {
          await apiGraph.patch(
            `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/outcomes/${outcome.id}`,
            {
              "@odata.type": "#microsoft.graph.educationFeedbackOutcome",
              feedback: {
                text: outcome.feedback.text
              }
            }
          );
        }

        if (outcome["@odata.type"] === "#microsoft.graph.educationPointsOutcome" && outcome.points) {
          await apiGraph.patch(
            `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/submissions/${submissionId}/outcomes/${outcome.id}`,
            {
              "@odata.type": "#microsoft.graph.educationPointsOutcome",
              points: {
                "@odata.type": "#microsoft.graph.educationAssignmentPointsGrade",
                points: parseInt(outcome.points.points)
              }
            }
          );
        }
      });

      await Promise.all(promises);
    }
  }
};
